import { useState, useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { authService } from '../services/authService';
import { getWsBaseUrl } from '../services/configService';
import { shouldFilterMessage, logFilteredMessage } from '../utils/messageFilter';

export interface AuthenticatedWebSocketOptions {
  onOpen?: () => void;
  onMessage?: (message: AuthenticatedWebSocketMessage) => void;
  onError?: (error: Event) => void;
  onClose?: () => void;
  onAuthError?: (error: string) => void;
}

export interface AuthenticatedWebSocketMessage {
  type: 'AuthenticatedConnected' | 'UserNotification' | 'QueueUpdate' | 'SessionUpdate' | 'ChargerUpdate' | 'ButtonStateUpdate' | 'ChargerSubscribed' | 'ChargerUnsubscribed' | 'PublicBroadcastSubscribed' | 'NotificationBroadcast' | 'RefreshChargersData' | 'ActivityEvent' | 'Error';
  data: any;
}

export interface ChargerSubscription {
  chargerId: string;
  isSubscribed: boolean;
}

/**
 * Custom hook for authenticated SignalR WebSocket connections
 * Provides user-specific real-time notifications and charger updates
 * Requires valid JWT authentication token
 */
export const useAuthenticatedWebSocket = (
  options: AuthenticatedWebSocketOptions = {}
) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [subscribedChargers, setSubscribedChargers] = useState<Set<string>>(new Set());
  const [connectionError, setConnectionError] = useState<string | null>(null);
  
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const authCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Check authentication status periodically
  const checkAuthenticationStatus = useCallback(() => {
    const isAuth = authService.isAuthenticated();
    setIsAuthenticated(isAuth);
    
    if (!isAuth && connectionRef.current) {
      console.log('🔒 Authentication lost - disconnecting WebSocket');
      connectionRef.current.stop();
    }
    
    return isAuth;
  }, []);
  
  const connect = useCallback(async () => {
    try {
      // Verify authentication before attempting connection
      if (!checkAuthenticationStatus()) {
        const error = 'Authentication required for authenticated WebSocket';
        console.warn('🔒', error);
        setConnectionError(error);
        if (options.onAuthError) options.onAuthError(error);
        return;
      }
      
      // Close existing connection
      if (connectionRef.current) {
        await connectionRef.current.stop();
      }
      
      setConnectionError(null);
      const hubUrl = getAuthenticatedHubUrl();
      const token = authService.getToken();
      
      if (!token) {
        const error = 'No authentication token available';
        console.error('🔒', error);
        setConnectionError(error);
        if (options.onAuthError) options.onAuthError(error);
        return;
      }
      
      console.log('🔗 Connecting to authenticated SignalR hub:', hubUrl);
      
      // Create connection with JWT token in query parameter
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          skipNegotiation: false,
          accessTokenFactory: () => token, // SignalR will add this as ?access_token=
          transport: signalR.HttpTransportType.WebSockets | 
                    signalR.HttpTransportType.ServerSentEvents | 
                    signalR.HttpTransportType.LongPolling,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 20000, 30000])
        .configureLogging(signalR.LogLevel.Information)
        .build();
      
      connectionRef.current = connection;
      
      // Set up connection event handlers
      connection.onclose((error) => {
        console.log('🔌 Authenticated SignalR connection closed:', error);
        setIsConnected(false);
        if (options.onClose) options.onClose();
      });
      
      connection.onreconnecting((error) => {
        console.log('🔄 Authenticated SignalR reconnecting:', error);
        setIsConnected(false);
      });
      
      connection.onreconnected((connectionId) => {
        console.log('✅ Authenticated SignalR reconnected:', connectionId);
        setIsConnected(true);
        
        // Re-subscribe to chargers after reconnection
        subscribedChargers.forEach(async (chargerId) => {
          try {
            await connection.invoke('JoinChargerGroup', chargerId);
            console.log('🔄 Re-subscribed to charger:', chargerId);
          } catch (error) {
            console.error('❌ Failed to re-subscribe to charger:', chargerId, error);
          }
        });
      });
      
      // Set up message handlers with filtering
      const sendFilteredMessage = (type: AuthenticatedWebSocketMessage['type'], data: any) => {
        const message = { type, data };
        
        // Apply centralized message filtering
        if (shouldFilterMessage(message)) {
          logFilteredMessage(message, `${type} message filtered at WebSocket level`);
          return;
        }
        
        if (options.onMessage) {
          options.onMessage(message);
        }
      };
      
      connection.on('AuthenticatedConnected', (data) => {
        // Reduced verbose logging - only log to console, not UI
        setIsConnected(true);
        setConnectionError(null);
        sendFilteredMessage('AuthenticatedConnected', data);
      });
      
      connection.on('UserNotification', (data) => {
        console.log('📨 User notification:', data);
        sendFilteredMessage('UserNotification', data);
      });
      
      connection.on('QueueUpdate', (data) => {
        console.log('📨 Queue update:', data);
        sendFilteredMessage('QueueUpdate', data);
      });
      
      connection.on('SessionUpdate', (data) => {
        console.log('📨 Session update:', data);
        sendFilteredMessage('SessionUpdate', data);
      });
      
      connection.on('ChargerUpdate', (data) => {
        console.log('📨 Charger update:', data);
        sendFilteredMessage('ChargerUpdate', data);
      });
      
      connection.on('ButtonStateUpdate', (data) => {
        console.log('📨 Button state update:', data);
        sendFilteredMessage('ButtonStateUpdate', data);
      });

      // Public data events (previously handled by anonymous connection)
      connection.on('ChargerSubscribed', (data) => {
        console.log('📨 Charger subscribed:', data);
        sendFilteredMessage('ChargerSubscribed', data);
      });

      connection.on('ChargerUnsubscribed', (data) => {
        console.log('📨 Charger unsubscribed:', data);
        sendFilteredMessage('ChargerUnsubscribed', data);
      });

      connection.on('PublicBroadcastSubscribed', (data) => {
        console.log('📨 Public broadcast subscribed:', data);
        sendFilteredMessage('PublicBroadcastSubscribed', data);
      });

      connection.on('NotificationBroadcast', (data) => {
        console.log('📨 Public notification broadcast:', data);
        sendFilteredMessage('NotificationBroadcast', data);
      });

      connection.on('Error', (data) => {
        console.error('📨 SignalR error:', data);
        sendFilteredMessage('Error', data);
      });

      // Handle refresh chargers data signal - triggers data refresh for Charging Queue screen only
      connection.on('RefreshChargersData', (data) => {
        console.log('🔄 Refresh chargers data signal:', data);
        sendFilteredMessage('RefreshChargersData', data);
      });

      // Handle charger activity feed events (join/start/stop/etc.) for the Activity page.
      connection.on('ActivityEvent', (data) => {
        console.log('📨 Activity event:', data);
        sendFilteredMessage('ActivityEvent', data);
      });

      connection.on('Error', (error) => {
        console.error('📨 SignalR error:', error);
        setConnectionError(error);
        sendFilteredMessage('Error', error);
      });
      
      // Start connection
      await connection.start();
      // Reduced verbose logging
      setIsConnected(true);
      setConnectionError(null);
      
      if (options.onOpen) options.onOpen();
      
    } catch (error) {
      console.error('❌ Authenticated SignalR connection failed:', error);
      setIsConnected(false);
      setConnectionError(error instanceof Error ? error.message : 'Connection failed');
      
      if (options.onError) {
        options.onError(error as Event);
      }
      
      // Retry connection after delay
      reconnectTimeoutRef.current = setTimeout(() => {
        console.log('🔄 Retrying authenticated SignalR connection...');
        connect();
      }, 5000);
    }
  }, [options, subscribedChargers, checkAuthenticationStatus]);
  
  // Helper function to get authenticated hub URL
  const getAuthenticatedHubUrl = useCallback(() => {
    // Use runtime configuration service
    const wsBaseUrl = getWsBaseUrl();
    return `${wsBaseUrl}/hubs/notifications`;
  }, []);
  
  // Subscribe to charger updates
  const subscribeToCharger = useCallback(async (chargerId: string) => {
    if (!connectionRef.current || !isConnected) {
      console.warn('🔗 Cannot subscribe to charger - not connected');
      return false;
    }
    
    try {
      await connectionRef.current.invoke('JoinChargerGroup', chargerId);
      setSubscribedChargers(prev => new Set(prev).add(chargerId));
      console.log('✅ Subscribed to charger:', chargerId);
      return true;
    } catch (error) {
      console.error('❌ Failed to subscribe to charger:', chargerId, error);
      return false;
    }
  }, [isConnected]);
  
  // Unsubscribe from charger updates
  const unsubscribeFromCharger = useCallback(async (chargerId: string) => {
    if (!connectionRef.current || !isConnected) {
      console.warn('🔗 Cannot unsubscribe from charger - not connected');
      return false;
    }
    
    try {
      await connectionRef.current.invoke('LeaveChargerGroup', chargerId);
      setSubscribedChargers(prev => {
        const newSet = new Set(prev);
        newSet.delete(chargerId);
        return newSet;
      });
      console.log('✅ Unsubscribed from charger:', chargerId);
      return true;
    } catch (error) {
      console.error('❌ Failed to unsubscribe from charger:', chargerId, error);
      return false;
    }
  }, [isConnected]);
  
  // Mark notification as read
  const markNotificationRead = useCallback(async (notificationId: string) => {
    if (!connectionRef.current || !isConnected) {
      console.warn('🔗 Cannot mark notification as read - not connected');
      return false;
    }
    
    try {
      await connectionRef.current.invoke('MarkNotificationRead', notificationId);
      console.log('✅ Marked notification as read:', notificationId);
      return true;
    } catch (error) {
      console.error('❌ Failed to mark notification as read:', notificationId, error);
      return false;
    }
  }, [isConnected]);
  
  // Request full state synchronization
  const requestStateSync = useCallback(async () => {
    if (!connectionRef.current || !isConnected) {
      console.warn('🔗 Cannot request state sync - not connected');
      return false;
    }
    
    try {
      await connectionRef.current.invoke('RequestStateSync');
      console.log('✅ Requested state synchronization');
      return true;
    } catch (error) {
      console.error('❌ Failed to request state sync:', error);
      return false;
    }
  }, [isConnected]);

  // Subscribe to public broadcast (system announcements)
  const subscribeToPublicBroadcast = useCallback(async () => {
    if (!connectionRef.current || !isConnected) {
      console.warn('🔗 Cannot subscribe to public broadcast - not connected');
      return false;
    }
    
    try {
      await connectionRef.current.invoke('JoinPublicBroadcast');
      console.log('✅ Subscribed to public broadcast');
      return true;
    } catch (error) {
      console.error('❌ Failed to subscribe to public broadcast:', error);
      return false;
    }
  }, [isConnected]);
  
  // Connect on mount and when authentication status changes
  useEffect(() => {
    if (isAuthenticated) {
      connect();
    } else {
      // Disconnect if not authenticated
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
      setIsConnected(false);
    }
    
    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect, isAuthenticated]);
  
  // Set up authentication status checking
  useEffect(() => {
    checkAuthenticationStatus();
    
    // Check authentication status periodically
    authCheckIntervalRef.current = setInterval(checkAuthenticationStatus, 30000); // Every 30 seconds
    
    return () => {
      if (authCheckIntervalRef.current) {
        clearInterval(authCheckIntervalRef.current);
      }
    };
  }, [checkAuthenticationStatus]);
  
  return { 
    isConnected,
    isAuthenticated,
    connectionError,
    subscribedChargers: Array.from(subscribedChargers),
    subscribeToCharger,
    unsubscribeFromCharger,
    markNotificationRead,
    requestStateSync,
    subscribeToPublicBroadcast
  };
};